import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import useQuestsAPI from "../../../hooks/useQuestsAPI";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TextField, FormHelperText, Rating } from "@mui/material";
import useQuestSubmissionAPI from "../../../hooks/useQuestSubmissionAPI.js";
import { TextFieldCustomization } from "./materialUI customization.js";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import useAccountProgressAPI from "../../../hooks/useGetAccountProgressAPI.js";
import CompleteAccountModal from "./components/CompleteAccountModal.js";
import { useRamadan } from "../../../context/RamadanContext";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";

/**
 * QuestRegistration Component
 * Component to show inputs required for submission for a form while also handling the required API requests.
 *
 * @returns {JSX.Element} The rendered component.
 */
const QuestSubmission = () => {
  const { isRamadan } = useRamadan();

  const navigate = useNavigate();
  const { user } = useAuth0();

  const { progress } = useAccountProgressAPI();
  const isClient = user.role === "client";
  const [openModal, setOpenModal] = useState(false);

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  // Watch rating values to update display
  const rateDataValue = watch("rateData", 0);
  const ratePrizeValue = watch("ratePrize", 0);
  const rateInteractionValue = watch("rateInteraction", 0);
  const rateSalaryValue = watch("rateSalary", 0);
  const ratePreferenceValue = watch("ratePreference", 0);

  // Get quest data
  const { id } = useParams();
  const { isLoading, data, error } = useQuestsAPI(id);
  // Handle if enter invalid id in url
  const res = error ? error.response : null;
  useEffect(() => {
    if (res && res.status === 404) {
      navigate("/page-error-404");
    }
  }, [res, navigate]);

  // Initialize quest submission custom hook and check if user is registered and approved
  const { questSubmissionRequest, isLoading: isSubmissionLoading } =
    useQuestSubmissionAPI(id);

  // Flag if submission has ended
  let phaseEnded =
    data?.phase !== "submission" || data?.submissionDeadline < new Date();

  if (data.type === "race" && data.phase === "registration") {
    phaseEnded = false;
  }

  const validateRatings = (formData) => {
    const missingRatings = [];

    // Check required ratings based on quest type
    if (!formData.rateData || formData.rateData === 0) {
      missingRatings.push("Data rating");
    }

    if (data.isHiring) {
      if (!formData.rateSalary || formData.rateSalary === 0) {
        missingRatings.push("Salary rating");
      }
      if (!formData.ratePreference || formData.ratePreference === 0) {
        missingRatings.push("Hiring preference rating");
      }
    } else {
      if (!formData.ratePrize || formData.ratePrize === 0) {
        missingRatings.push("Prize rating");
      }
      if (!formData.rateInteraction || formData.rateInteraction === 0) {
        missingRatings.push("Group interaction rating");
      }
    }

    return missingRatings;
  };

  // Replace your existing onSubmit function with this enhanced version
  const onSubmit = async (formData) => {
    if (progress < 100) {
      setOpenModal(true);
      return;
    }

    // Validate ratings before submission
    const missingRatings = validateRatings(formData);

    if (missingRatings.length > 0) {
      const message =
        missingRatings.length === 1
          ? `Please provide a rating for: ${missingRatings[0]}`
          : `Please provide ratings for: ${missingRatings.join(", ")}`;

      toast.error(message);
      return;
    }

    try {
      // Prepare submission data based on quest type
      let submissionData = { ...data, ...formData };

      await questSubmissionRequest(submissionData);

      toast.success("Submitted");
      setTimeout(() => {
        navigate("/quest-list");
      }, 3500);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  // Custom input styling to match the image
  const customInputStyle = {
    "& .MuiInputBase-root": {
      backgroundColor: "#f8f9fa",
      border: "1px solid #888888",
      borderRadius: "8px",
      padding: "12px 16px",
      fontSize: "14px",
      color: "#6c757d",
    },
    "& .MuiInputBase-input": {
      padding: "0",
      "&::placeholder": {
        color: "#6c757d",
        opacity: 1,
      },
    },
    "& .MuiInputLabel-root": {
      color: "#374151",
      fontSize: "14px",
      fontWeight: "500",
      transform: "translate(0, -1.5px) scale(1)",
      "&.Mui-focused": {
        color: "#007bff",
      },
    },
    "& .MuiInput-underline:before": {
      display: "none",
    },
    "& .MuiInput-underline:after": {
      display: "none",
    },
  };

  const textareaStyle = {
    backgroundColor: "#f8f9fa",
    border: "1px solid #888888",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    color: "#6c757d",
    resize: "none",
    width: "100%",
    "&:focus": {
      outline: "none",
      borderColor: "#007bff",
      backgroundColor: "#fff",
    },
    "&::placeholder": {
      color: "#6c757d",
    },
  };

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap justify-content-end">
        <Button
          className="me-2"
          variant="outline-primary btn-rounded"
          onClick={() => navigate(`/quest-view/${id}`)}
        >
          <i className="fa-solid fa-chevron-left"></i>{" "}
          <span style={{ margin: "0 10px" }}>Back</span>
        </Button>
      </div>

      <div className="quest-view">
        <div className="card w-100">
          {/* WAIT FOR RESPONSE */}
          {isLoading ? (
            <div className="card-header d-flex align-items-center justify-content-start gap-5 flex-wrap">
              <h3 className="mb-4 me-auto">Loading...</h3>
            </div>
          ) : (
            <>
              <div className="card-header d-flex align-items-center justify-content-start gap-5 flex-wrap">
                <h3>{`${id} - ${data.title} Quest`}</h3>
                {phaseEnded && (
                  <h3 className="color-primary">Submission Phase Ended!</h3>
                )}
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* VIDEO & FILES */}
                <div className="card-body" style={{ maxWidth: "700px" }}>
                  <h3>Submission Files</h3>

                  <div className="mb-3">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Project File URL *
                    </label>
                    <Controller
                      name="projectFilesUrl"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="https://figma.com/... or https://github.com/..."
                          variant="standard"
                          sx={customInputStyle}
                          fullWidth
                          type="url"
                          error={!!errors.projectFilesUrl}
                          helperText={
                            errors.projectFilesUrl
                              ? "Project Files URL is required"
                              : ""
                          }
                        />
                      )}
                      rules={{ required: true }}
                    />
                    {/* Warning reminder for Project File URL */}
                    <div
                      style={{
                        backgroundColor: "rgb(255 221 184 / 66%)",
                        border: "1px solid #F93A0B",
                        borderRadius: "6px",
                        padding: "12px",
                        marginTop: "8px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: "rgb(255, 237, 213)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      >
                        <i
                          className="bi bi-exclamation-circle"
                          style={{ color: "#F93A0B", fontSize: "1.3rem" }}
                        ></i>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#F93A0B",
                          lineHeight: "1.4",
                        }}
                      >
                        Your project should be compressed then shared. Read
                        help? Contact us on the WhatsApp group.
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Video Walkthrough URL *
                    </label>
                    <Controller
                      name="videoWalkthroughUrl"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="https://youtube.com/...or https://drive.google.com/..."
                          variant="standard"
                          sx={customInputStyle}
                          fullWidth
                          type="url"
                          error={!!errors.videoWalkthroughUrl}
                          helperText={
                            errors.videoWalkthroughUrl
                              ? "Video Walkthrough URL is required"
                              : ""
                          }
                        />
                      )}
                      rules={{ required: true }}
                    />
                    {/* Warning reminder for Video Walkthrough URL */}
                    <div
                      style={{
                        backgroundColor: "rgb(255 221 184 / 66%)",
                        border: "1px solid #F93A0B",
                        borderRadius: "6px",
                        padding: "12px",
                        marginTop: "8px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: "rgb(255, 237, 213)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      >
                        <i
                          className="bi bi-exclamation-circle"
                          style={{ color: "#F93A0B", fontSize: "1.3rem" }}
                        ></i>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          color: "#F93A0B",
                          lineHeight: "1.4",
                        }}
                      >
                        This video is your chance to show real applications
                        without a preset walkthrough are much less likely to
                        succeed. Take a few minutes to explain your work and let
                        it shine.
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Additional Notes
                    </label>
                    <Controller
                      name="submissionNote"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <textarea
                          {...field}
                          style={textareaStyle}
                          rows={3}
                          placeholder="Any additional notes you want to share with the reviewer..."
                        />
                      )}
                      rules={{ required: false }}
                    />
                  </div>
                </div>

                {/* FEEDBACK */}
                <div
                  className="card-body pt-0 user-feedback"
                  style={{
                    maxWidth: `${data.isHiring ? "1090px" : "700px"}`,
                    minWidth: "300px",
                  }}
                >
                  <h3>Feedback</h3>

                  <div className="">
                    <div className="mb-4">
                      <div
                        className={`d-flex mb-2 ${
                          data.isHiring
                            ? "feedback-field-hiring"
                            : "feedback-field-task"
                        }`}
                      >
                        <p
                          className="feedback-slider mb-0 me-3"
                          style={{
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          How would you rate the provided data?
                        </p>
                        <span className="d-flex flex-direction-row align-items-center">
                          <Controller
                            name="rateData"
                            control={control}
                            defaultValue={0}
                            rules={{ required: true, min: 1 }}
                            render={({ field }) => (
                              <Rating
                                {...field}
                                name="rateData"
                                size="medium"
                                sx={{
                                  color: "#ffc107",
                                  "& .MuiRating-iconEmpty": {
                                    color: "#888888",
                                  },
                                }}
                                onChange={(event, newValue) => {
                                  field.onChange(newValue);
                                }}
                              />
                            )}
                          />
                          <span
                            className="ms-2"
                            style={{
                              color: "#6c757d",
                              fontSize: "14px",
                            }}
                          >
                            ({rateDataValue || 0}/5)
                          </span>
                          {errors.rateData && (
                            <span
                              className="ms-2"
                              style={{
                                color: "#dc3545",
                                fontSize: "12px",
                                fontWeight: "500",
                              }}
                            >
                              Rating required
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="form-group">
                        <Controller
                          name="rateDataReason"
                          control={control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <>
                              <textarea
                                style={textareaStyle}
                                {...field}
                                rows="3"
                                placeholder="Please explain your rating..."
                              />
                              {errors.rateDataReason && (
                                <FormHelperText error>
                                  This field is required.
                                </FormHelperText>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </div>

                    {data.isHiring ? (
                      <>
                        <div className="mb-4">
                          <div className="d-flex mb-2 feedback-field-hiring">
                            <p
                              className="feedback-slider mb-0 me-3"
                              style={{
                                color: "#374151",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              How satisfied are you with the salary that
                              offered?
                            </p>
                            <span className="d-flex flex-direction-row align-items-center">
                              <Controller
                                name="rateSalary"
                                control={control}
                                defaultValue={0}
                                rules={{ required: true, min: 1 }}
                                render={({ field }) => (
                                  <Rating
                                    {...field}
                                    name="rateSalary"
                                    size="medium"
                                    sx={{
                                      color: "#ffc107",
                                      "& .MuiRating-iconEmpty": {
                                        color: "#888888",
                                      },
                                    }}
                                    onChange={(event, newValue) => {
                                      field.onChange(newValue);
                                    }}
                                  />
                                )}
                              />
                              <span
                                className="ms-2"
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                }}
                              >
                                ({rateSalaryValue || 0}/5)
                              </span>
                              {errors.rateSalary && (
                                <span
                                  className="ms-2"
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Rating required
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="form-group">
                            <Controller
                              name="rateSalaryReason"
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <>
                                  <textarea
                                    style={textareaStyle}
                                    {...field}
                                    rows="3"
                                    placeholder="Please explain your rating..."
                                  />
                                  {errors.rateSalaryReason && (
                                    <FormHelperText error>
                                      This field is required.
                                    </FormHelperText>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="d-flex mb-2 feedback-field-hiring">
                            <p
                              className="feedback-slider mb-0 me-3"
                              style={{
                                color: "#374151",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              How much do you prefer task-based hiring (like
                              Code Quests) over traditional hiring CV-based
                              hiring?
                            </p>
                            <span className="d-flex flex-direction-row align-items-center">
                              <Controller
                                name="ratePreference"
                                control={control}
                                defaultValue={0}
                                rules={{ required: true, min: 1 }}
                                render={({ field }) => (
                                  <Rating
                                    {...field}
                                    name="ratePreference"
                                    size="medium"
                                    sx={{
                                      color: "#ffc107",
                                      "& .MuiRating-iconEmpty": {
                                        color: "#888888",
                                      },
                                    }}
                                    onChange={(event, newValue) => {
                                      field.onChange(newValue);
                                    }}
                                  />
                                )}
                              />
                              <span
                                className="ms-2"
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                }}
                              >
                                ({ratePreferenceValue || 0}/5)
                              </span>
                              {errors.ratePreference && (
                                <span
                                  className="ms-2"
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Rating required
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="form-group">
                            <Controller
                              name="ratePreferenceReason"
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <>
                                  <textarea
                                    style={textareaStyle}
                                    {...field}
                                    rows="3"
                                    placeholder="Please explain your rating..."
                                  />
                                  {errors.ratePreferenceReason && (
                                    <FormHelperText error>
                                      This field is required.
                                    </FormHelperText>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-4">
                          <div className="d-flex mb-2 feedback-field-task">
                            <p
                              className="feedback-slider mb-0 me-3"
                              style={{
                                color: "#374151",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              How satisfied are you with the prizes?
                            </p>
                            <span className="d-flex flex-direction-row align-items-center">
                              <Controller
                                name="ratePrize"
                                control={control}
                                defaultValue={0}
                                rules={{ required: true, min: 1 }}
                                render={({ field }) => (
                                  <Rating
                                    {...field}
                                    name="ratePrize"
                                    size="medium"
                                    sx={{
                                      color: "#ffc107",
                                      "& .MuiRating-iconEmpty": {
                                        color: "#888888",
                                      },
                                    }}
                                    onChange={(event, newValue) => {
                                      field.onChange(newValue);
                                    }}
                                  />
                                )}
                              />
                              <span
                                className="ms-2"
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                }}
                              >
                                ({ratePrizeValue || 0}/5)
                              </span>
                              {errors.ratePrize && (
                                <span
                                  className="ms-2"
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Rating required
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="form-group">
                            <Controller
                              name="ratePrizeReason"
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <>
                                  <textarea
                                    style={textareaStyle}
                                    {...field}
                                    rows="3"
                                    placeholder="Please explain your rating..."
                                  />
                                  {errors.ratePrizeReason && (
                                    <FormHelperText error>
                                      This field is required.
                                    </FormHelperText>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="d-flex mb-2 feedback-field-task">
                            <p
                              className="feedback-slider mb-0 me-3"
                              style={{
                                color: "#374151",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              How would you rate the group interaction?
                            </p>
                            <span className="d-flex flex-direction-row align-items-center">
                              <Controller
                                name="rateInteraction"
                                control={control}
                                defaultValue={0}
                                rules={{ required: true, min: 1 }}
                                render={({ field }) => (
                                  <Rating
                                    {...field}
                                    name="rateInteraction"
                                    size="medium"
                                    sx={{
                                      color: "#ffc107",
                                      "& .MuiRating-iconEmpty": {
                                        color: "#888888",
                                      },
                                    }}
                                    onChange={(event, newValue) => {
                                      field.onChange(newValue);
                                    }}
                                  />
                                )}
                              />
                              <span
                                className="ms-2"
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                }}
                              >
                                ({rateInteractionValue || 0}/5)
                              </span>
                              {errors.rateInteraction && (
                                <span
                                  className="ms-2"
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Rating required
                                </span>
                              )}
                            </span>
                          </div>
                          <div className="form-group">
                            <Controller
                              name="rateInteractionReason"
                              control={control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <>
                                  <textarea
                                    style={textareaStyle}
                                    {...field}
                                    rows="3"
                                    placeholder="Please explain your rating..."
                                  />
                                  {errors.rateInteractionReason && (
                                    <FormHelperText error>
                                      This field is required.
                                    </FormHelperText>
                                  )}
                                </>
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mb-4">
                      <p
                        className="feedback-slider mb-2"
                        style={{
                          color: "#374151",
                          fontSize: "14px",
                          fontWeight: "500",
                          marginBottom: "12px",
                        }}
                      >
                        Do you have any additional feedback comments?
                      </p>
                      <div className="form-group">
                        <Controller
                          name="additionalComments"
                          control={control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <textarea
                              style={textareaStyle}
                              {...field}
                              rows="3"
                              placeholder="Share any other feedback or suggestions..."
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {/* SUBMIT */}
                <div className="card-footer border-0">
                  {!phaseEnded && (
                    <button
                      className={`btn btn-primary px-5 mb-4 fs-14 ${
                        isRamadan && "ramadan-button"
                      }`}
                      type="submit"
                      disabled={isSubmissionLoading || isClient}
                    >
                      {isRamadan && <div></div>}
                      <span className="fs-14" style={{ fontWeight: "bold" }}>
                        SUBMIT
                      </span>
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Complete Account Modal */}
      {openModal && <CompleteAccountModal setOpenModal={setOpenModal} />}
    </>
  );
};

export default QuestSubmission;
